import classNames from 'classnames';
import React from 'react';
import { isSafari } from 'react-device-detect';
import { Row } from 'react-table';
import { Nullable } from 'src/types/nullable.type';
import { BaseTable, BaseTableProps } from './base-table';

export function processArrayOrFunction<F extends Function, T>(v: T[] | undefined | F, applier: (f: F) => T[]): T[] {
  let result: T[] = [];
  if (v) {
    if (Array.isArray(v)) {
      result = v;
    } else if (typeof v === 'function') {
      result = applier(v);
    }
  }
  return result;
}

export function processBooleanOrFunction<F extends Function>(
  v: boolean | undefined | F,
  applier: (f: F) => boolean
): boolean {
  let result: boolean = false;
  if (v) {
    if (typeof v === 'boolean') {
      result = v;
    } else if (typeof v === 'function') {
      result = applier(v);
    }
  }
  return result;
}

type ColumnOptions = Nullable<{
  disableHoverStyle?: boolean;
}>;

export type DataTableProps = Pick<
  BaseTableProps,
  | 'columns'
  | 'data'
  | 'onRowClick'
  | 'expandRowsAtIndexes'
  | 'forceExpandByIndex'
  | 'expandOnlySingleRow'
  | 'expandAllRowsByDefault'
  | 'renderRowSubComponent'
  | 'hideHeaders'
  | 'tableClassName'
  | 'onToggleExpandedRow'
  | 'tableHeaderClassName'
> & {
  disableCheckDataOnExpand?: boolean;
  disableSubComponentStyle?: boolean;
  disableRowCursorPointer?: boolean | ((row: Row, rowIndex: number) => boolean);
  rowAlignTop?: boolean;
  columnClasses?: string[];
  columnOptions?: ColumnOptions[] | ((row: Row, rowIndex: number) => ColumnOptions[]);
};

const hoverBottomBorderClasses =
  'after:absolute after:h-[3px] after:transition-colors after:duration-200 after:-bottom-[2.5px] after:inset-x-0 after:z-10 hover:after:bg-bedrock-white';

const hoverTopBorderClasses =
  'before:absolute before:h-[3px] before:transition-colors before:duration-200 before:-top-[2.5px] before:inset-x-0 before:z-10';

export const DataTable = (props: DataTableProps) => {
  const {
    disableRowCursorPointer = false,
    disableSubComponentStyle,
    disableCheckDataOnExpand,
    rowAlignTop = true,
    columnClasses = [],
    columnOptions = [],
    tableHeaderClassName,
    ...restProps
  } = props;
  return (
    <BaseTable
      {...restProps}
      tableHeaderClassName={classNames('mb-2 bg-bedrock-white', tableHeaderClassName)}
      tableHeaderCellClassName='text-left text-bedrock-p-strong text-bedrock-black pb-2 first:pl-2'
      tableBodyRowClassName={(row: any, index) => {
        const processedDisableRowCursorPointer = processBooleanOrFunction(disableRowCursorPointer, (f) =>
          f(row, index)
        );
        return classNames(
          'relative group bg-bedrock-white transition-colors',
          'group-hover:after:bg-bedrock-white',
          'text-bedrock-p text-bedrock-black',
          {
            [hoverBottomBorderClasses]: !isSafari,
            'after:bg-bedrock-white after:bottom-auto after:-top-[3px]': disableCheckDataOnExpand
              ? row.isExpanded
              : row.original.expandable && row.isExpanded,
            'cursor-pointer': !processedDisableRowCursorPointer,
            'align-top': rowAlignTop,
          }
        );
      }}
      tableBodyCellClassName={(row: any, rowIndex, cellIndex) => {
        const processedColumnOptions: ColumnOptions[] = processArrayOrFunction(columnOptions, (f) => f(row, rowIndex));
        return classNames(
          'first:rounded-l-lg first:pl-2 pl-0',
          'transition-colors duration-200',
          'border-t border-bedrock-light-gray',
          'p-2 group-hover:border-bedrock-white',
          'last:rounded-r-lg last:py-[7px]',
          {
            [`relative group-hover:before:bg-bedrock-white group-hover:after:bg-bedrock-white ${hoverTopBorderClasses} ${hoverBottomBorderClasses}`]:
              isSafari,
            'before:bg-bedrock-white':
              isSafari && !disableSubComponentStyle ? row.original.expandable && row.isExpanded : row.isExpanded,
            [`${!disableSubComponentStyle ? 'first:rounded-bl-none last:rounded-br-none' : ''} bg-bedrock-light-gray`]:
              !disableCheckDataOnExpand ? row.original.expandable && row.isExpanded : row.isExpanded,
            [columnClasses[cellIndex]]: columnClasses.length > cellIndex,
            'group-hover:border-transparent group-hover:bg-bedrock-light-gray':
              cellIndex >= processedColumnOptions.length || !processedColumnOptions[cellIndex]?.disableHoverStyle,
          }
        );
      }}
      tableBodyRowSubComponentClassName={classNames({
        'px-4 py-4': disableSubComponentStyle,
        'border-2 border-bedrock-light-gray': !disableSubComponentStyle,
        'px-6 pb-6 pt-[calc(1.5rem+3px)] mb-4 -mt-[3px]': !disableSubComponentStyle,
        'rounded-t-none rounded-lg': !disableSubComponentStyle,
        'after:absolute after:h-px after:bg-bedrock-white after:-bottom-px after:inset-x-0 after:z-10':
          !disableSubComponentStyle,
      })}
    />
  );
};
